import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { CreditBalancePayload } from '@voice-assistant/shared';

// ════════════════════════════════════════════════════════════════════════════
// Credits FSM store — valko-credits-platform Phase 2 (balance surface).
//
// The frontend face of the credits ledger: renders the server-pushed wallet
// snapshot (WsEvent.CREDIT_BALANCE) and the stage-5 gate denial (FEATURE_GATED
// with reason 'insufficient_balance' → top-up modal). Workspace Frontend FSM
// pattern (server-config/docs/frontend-fsm-pattern.md), five elements:
//   1. Explicit FSM         — the CreditsPhase union + exported TRANSITIONS (R-A1).
//   2. Single store         — wallet region (per user) + orthogonal top-up-modal
//                             region live HERE; components are pure renderers of
//                             the selectors below.
//   3. Server-authoritative — onCreditBalance is the ONLY balance writer. It
//                             OVERWRITES the wallet with the server snapshot,
//                             never merges local guesses, and the store NEVER
//                             synthesizes a balance: until a CREDIT_BALANCE
//                             event arrives THIS session (`live`, session-scoped,
//                             never persisted) every selector reports
//                             'uninitialized' / null / pill-hidden — zero visual
//                             change for dogfood users whose tier never emits.
//   4. Named actions (R-A2) — the wire layer (the socket hook) calls
//                             onCreditBalance / onFeatureGated directly; no
//                             CustomEvent bus anywhere.
//   5. Setpoint evals       — setpoint-evals/credits-fsm/ drives THIS store
//                             (simulator + one scenario per TRANSITIONS row).
//
// ORIGIN-KEYED (R-A4 store in the canonical dir): `wallets` is keyed by the
// authenticated user identity (email — the workspace ACL key) + `activeUserId`
// bound via bindUser(). A CREDIT_BALANCE that lands before auth resolves is
// BUFFERED (pendingPayload) and applied on bind — server data is never dropped
// and never rendered without a known origin.
//
// PERSISTED (numbers only, continuity across reloads): `partialize` stores the
// wallets with `live` forced false, so a rehydrated wallet is data continuity,
// never a live balance claim — the pill stays dark until the server speaks.
// ════════════════════════════════════════════════════════════════════════════

// ─── Types ─────────────────────────────────────────────────────────────────────

/**
 * Wallet-region FSM phase (per activeUserId):
 *   uninitialized  no CREDIT_BALANCE this session (fresh, rehydrated, or unbound)
 *                  — NO pill, NO balance claim.
 *   ok             live, available (balanceCenti - heldCenti) ≥ low threshold.
 *   low            live, available below the threshold (10% of the last known
 *                  top-up pack, else LOW_BALANCE_FALLBACK_CENTI).
 */
export type CreditsPhase = 'uninitialized' | 'ok' | 'low';

export interface CreditsWallet {
  /** Server-reported balance (centicredits), holds included. */
  balanceCenti: number;
  /** Centicredits reserved by open (unreleased) holds. */
  heldCenti: number;
  /** Size of the last observed top-up (reason:'topup' deltaCenti) — the low
   *  threshold is floor(0.1 × this); null until a pack has been observed. */
  lastTopUpPackCenti: number | null;
  /** True iff a CREDIT_BALANCE arrived THIS session. Session-scoped — persisted
   *  as false (partialize), so rehydrated numbers are never a live claim. */
  live: boolean;
  /** Wallet FSM phase (meaningful only while live — selectors guard on it). */
  phase: CreditsPhase;
  /** Increments ONLY on an (uninitialized|ok) → low edge — the low-balance
   *  notice fires once per crossing, never per event (every settle emits). */
  lowBalanceCrossings: number;
  /** Crossings the user has dismissed (acknowledgeLowBalance) — the notice
   *  renderer shows when lowBalanceCrossings > acknowledgedCrossings. */
  acknowledgedCrossings?: number;
  /** Increments ONLY on a server-authoritative TOP-UP fact observed while a valid
   *  pending-topup marker is live (fast path: reason==='topup' deltaCenti>0; backstop:
   *  the GET /billing/credits/balance?since topup-since fact). A DERIVED counter on the
   *  same 'credit_balance' trigger as lowBalanceCrossings — NOT a new FSM row (R-A1). The
   *  success notice fires once per crossing. NEVER increments on a balance DIFF (spoofable). */
  topupSuccessCrossings?: number;
  /** Crossings the user has dismissed (acknowledgeTopupSuccess) — the success notice
   *  shows when topupSuccessCrossings > acknowledgedTopupCrossings. */
  acknowledgedTopupCrossings?: number;
  /** Credits (centi) to DISPLAY in the success toast, captured from the pending-topup
   *  marker's packCenti at fire time. Marker-sourced (NOT the server delta) so the shown
   *  number is identical whether the fast path (delta = paid+bonus) or the backstop
   *  (topup-since = paid only) resolved it. */
  lastTopupSuccessCenti?: number;
}

/** Persisted, origin-keyed pending-topup marker: set by markTopupInitiated the instant
 *  before the buy hard-nav (component state dies with the nav; only this persisted store
 *  survives). Gate (a) of the two-gate topup-confirmation: a valid unexpired marker whose
 *  userId matches the active/binding user MUST exist before any topup crossing can fire.
 *  NO prevBalance — the confirmation is NEVER a balance diff (that is spoofable). */
export interface PendingTopup {
  userId: string;
  /** Total credits the pack adds (paid + bonus), in centicredits — the DISPLAY amount. */
  packCenti: number;
  /** Epoch ms when the buy was initiated — TTL anchor + the poll `since` cursor. */
  ts: number;
}

export interface CreditsStore {
  // ── Wallet region (origin-keyed) ──────────────────────────────────────────
  wallets: Record<string, CreditsWallet>;
  activeUserId: string | null;
  /** CREDIT_BALANCE that arrived before bindUser (latest wins). Transient. */
  pendingPayload: CreditBalancePayload | null;

  // ── Top-up modal region (orthogonal to the wallet phase) ──────────────────
  topUpModalOpen: boolean;
  /** The featureKey the stage-5 denial gated — the modal's deep-link context. */
  gatedFeatureKey: string | null;

  // ── Pending-topup marker (persisted; survives the buy hard-nav) ────────────
  /** Set before the buy redirect; consumed when a server topup fact fires the
   *  confirmation, or cleared when the ~30-min TTL lapses. NEVER a balance baseline. */
  pendingTopup: PendingTopup | null;

  // ── Named actions (R-A2 — called directly by the wire/auth layers) ────────
  /** Auth resolved → bind the origin key; applies a buffered payload if any. */
  bindUser: (userId: string) => void;
  /** THE balance writer: server-authoritative reconcile from CREDIT_BALANCE.
   *  Overwrites persisted/local values; buffers when no user is bound yet. */
  onCreditBalance: (payload: CreditBalancePayload) => void;
  /** FEATURE_GATED router: opens the top-up modal ⇔ reason is exactly
   *  'insufficient_balance' (FeatureGate stage 5). Every other reason —
   *  tier / quota / profile_flag / absent — is a NO-OP for this store. */
  onFeatureGated: (payload: { featureKey: string; reason?: string }) => void;
  /** Open the top-up modal for a gated feature (modal:closed → modal:open). */
  openTopUpModal: (featureKey: string | null) => void;
  /** Close the top-up modal (modal:open → modal:closed). Idempotent. */
  closeTopUpModal: () => void;
  /** User dismissed the low-balance notice — records the crossing as seen. */
  acknowledgeLowBalance: () => void;

  // ── Topup-confirmation actions (R-A2 — named, no CustomEvent) ──────────────
  /** Buy handler calls this BEFORE the hard-nav to the edge checkout — persists the
   *  origin-keyed marker (gate a). NO balance snapshot is taken (the confirmation is
   *  never a diff). Overwrites any prior marker (a fresh buy supersedes an old one). */
  markTopupInitiated: (args: { userId: string; packCenti: number }) => void;
  /** Backstop path: a server topup-since fact from GET /billing/credits/balance?since.
   *  Fires the crossing ⇔ a valid unexpired marker for `userId` exists AND sinceCenti>0.
   *  A stale (TTL-lapsed) marker is cleared and never fires. sinceCenti<=0 keeps the
   *  marker intact (the webhook can lag the redirect — the caller keeps polling). */
  onTopupSinceFact: (args: { userId: string; sinceCenti: number }) => void;
  /** User (or the auto-dismiss) closed the success notice — records the crossing seen. */
  acknowledgeTopupSuccess: () => void;
}

// ─── Constants ─────────────────────────────────────────────────────────────────

/** Low-balance threshold (centicredits) before any top-up pack is observed. */
export const LOW_BALANCE_FALLBACK_CENTI = 100;

/** Pending-topup marker TTL (~30 min). A marker older than this is stale: it is IGNORED
 *  (never fires the confirmation) AND cleared on the next push/poll — a topup that never
 *  completed must not fire a success toast an hour later. */
export const TOPUP_MARKER_TTL_MS = 30 * 60 * 1000;

// ─── Transition table (R-A1) ───────────────────────────────────────────────────
//
// setpoint-evals/credits-fsm/SE-01-transitions-parity pins row-count == simulator
// scenario-count AND per-row `// SE-COVERS:` coverage — adding a row here MUST add
// a covering scenario in the same PR (R-A3). All wallet rows share the
// 'credit_balance' trigger (the server push is the only balance writer); the
// destination is decided by the payload vs the low threshold. The modal region is
// orthogonal: 'low' never auto-opens the modal — only a stage-5 gate denial does.

export const TRANSITIONS: Array<{ from: string; to: string; trigger: string }> = [
  { from: 'uninitialized', to: 'ok', trigger: 'credit_balance' },
  { from: 'uninitialized', to: 'low', trigger: 'credit_balance' },
  { from: 'ok', to: 'low', trigger: 'credit_balance' },
  { from: 'low', to: 'ok', trigger: 'credit_balance' },
  { from: 'ok', to: 'ok', trigger: 'credit_balance' },
  { from: 'modal:closed', to: 'modal:open', trigger: 'insufficient_balance_gated' },
  { from: 'modal:open', to: 'modal:closed', trigger: 'close_top_up_modal' },
];

// ─── Topup-marker evaluation (gate a × gate b) ────────────────────────────────

/** The two-gate topup verdict for a server balance event against the pending marker.
 *  Gate (a): a persisted marker exists for `userId` and is within the TTL.
 *  Gate (b) is the caller's payload — here we only report whether THIS event is a
 *  server-authoritative topup fact (reason:'topup' deltaCenti>0). BOTH gates must hold
 *  for the crossing to fire. `displayCenti` is the MARKER's packCenti (NOT the server
 *  delta) so the shown number is identical on the fast and backstop paths. */
interface TopupVerdict {
  /** The crossing should increment (gate a AND gate b both hold). */
  fired: boolean;
  /** The marker is present for this user but past its TTL — ignore AND clear it. */
  expired: boolean;
  /** Credits (centi) to display, sourced from the marker. */
  displayCenti: number;
}

function evalTopupMarker(
  pending: PendingTopup | null,
  userId: string,
  payload: CreditBalancePayload,
): TopupVerdict {
  const m = pending;
  if (!m || m.userId !== userId) return { fired: false, expired: false, displayCenti: 0 };
  const expired = Date.now() - m.ts >= TOPUP_MARKER_TTL_MS;
  const isTopupFact = payload.reason === 'topup' && (payload.deltaCenti ?? 0) > 0;
  return {
    fired: !expired && isTopupFact,
    expired,
    displayCenti: m.packCenti,
  };
}

// ─── Reconcile helper (the single wallet writer) ──────────────────────────────

/** Build the next wallet from the SERVER snapshot — overwrite, never merge. `topup`
 *  carries the two-gate verdict (see evalTopupMarker): on `fired` the DERIVED
 *  topupSuccessCrossings counter ticks once and the display amount is captured. It is
 *  NEVER a balance diff — only a server topup fact WITH a live marker ticks it. */
function reconcileWallet(
  prev: CreditsWallet | undefined,
  payload: CreditBalancePayload,
  topup: { fired: boolean; displayCenti: number } = { fired: false, displayCenti: 0 },
): CreditsWallet {
  const heldCenti = payload.heldCenti ?? 0;
  // A top-up teaches the store the user's pack size (threshold = 10% of it).
  const lastTopUpPackCenti =
    payload.reason === 'topup' && (payload.deltaCenti ?? 0) > 0
      ? (payload.deltaCenti as number)
      : prev?.lastTopUpPackCenti ?? null;
  const available = payload.balanceCenti - heldCenti;
  const threshold =
    lastTopUpPackCenti != null
      ? Math.floor(0.1 * lastTopUpPackCenti)
      : LOW_BALANCE_FALLBACK_CENTI;
  const phase: CreditsPhase = available < threshold ? 'low' : 'ok';
  // A persisted/stale wallet (live:false) is effectively 'uninitialized' — the
  // crossing edge is (uninitialized|ok) → low, once per crossing, never per event.
  const prevPhase: CreditsPhase = prev?.live ? prev.phase : 'uninitialized';
  const crossed = phase === 'low' && prevPhase !== 'low';
  return {
    balanceCenti: payload.balanceCenti,
    heldCenti,
    lastTopUpPackCenti,
    live: true,
    phase,
    lowBalanceCrossings: (prev?.lowBalanceCrossings ?? 0) + (crossed ? 1 : 0),
    acknowledgedCrossings: prev?.acknowledgedCrossings ?? 0,
    topupSuccessCrossings: (prev?.topupSuccessCrossings ?? 0) + (topup.fired ? 1 : 0),
    acknowledgedTopupCrossings: prev?.acknowledgedTopupCrossings ?? 0,
    lastTopupSuccessCenti: topup.fired ? topup.displayCenti : prev?.lastTopupSuccessCenti ?? 0,
  };
}

// ─── Store ─────────────────────────────────────────────────────────────────────

export const useCreditsStore = create<CreditsStore>()(
  persist(
    (set, get) => ({
      wallets: {},
      activeUserId: null,
      pendingPayload: null,
      topUpModalOpen: false,
      gatedFeatureKey: null,
      pendingTopup: null,

      bindUser: (userId) =>
        set((s) => {
          const next: Partial<CreditsStore> = { activeUserId: userId };
          if (s.pendingPayload) {
            // A server push landed before auth resolved — apply it now, verbatim.
            // RACE FIX (R-4b): evaluate the topup marker against the BINDING `userId`
            // argument, NOT s.activeUserId (still null inside this reducer). The common
            // ordering is webhook-beats-return → the reason:'topup' event was buffered
            // pre-bind, so THIS apply-path is where the fast-path confirmation fires.
            const v = evalTopupMarker(s.pendingTopup, userId, s.pendingPayload);
            next.wallets = {
              ...s.wallets,
              [userId]: reconcileWallet(s.wallets[userId], s.pendingPayload, v),
            };
            next.pendingPayload = null;
            // Consumed on fire; a TTL-lapsed marker is cleared on any push (never fires).
            if (v.fired || v.expired) next.pendingTopup = null;
          }
          return next;
        }),

      onCreditBalance: (payload) =>
        set((s) => {
          if (!s.activeUserId) {
            // Unbound: buffer (latest wins), stay dark — never guess an origin.
            return { pendingPayload: payload };
          }
          const id = s.activeUserId;
          const v = evalTopupMarker(s.pendingTopup, id, payload);
          const patch: Partial<CreditsStore> = {
            wallets: {
              ...s.wallets,
              [id]: reconcileWallet(s.wallets[id], payload, v),
            },
          };
          if (v.fired || v.expired) patch.pendingTopup = null;
          return patch;
        }),

      onFeatureGated: (payload) => {
        // Only a stage-5 balance denial concerns this store. Tier / quota /
        // profile_flag / absent reasons keep their existing paths (cascade
        // upgrade drawer, composer gated-bubble) — never guess a payment problem.
        if (payload?.reason !== 'insufficient_balance') return;
        get().openTopUpModal(payload.featureKey ?? null);
      },

      openTopUpModal: (featureKey) =>
        set({ topUpModalOpen: true, gatedFeatureKey: featureKey }),

      closeTopUpModal: () => set({ topUpModalOpen: false, gatedFeatureKey: null }),

      acknowledgeLowBalance: () =>
        set((s) => {
          const id = s.activeUserId;
          const wallet = id ? s.wallets[id] : undefined;
          if (!id || !wallet) return {};
          return {
            wallets: {
              ...s.wallets,
              [id]: { ...wallet, acknowledgedCrossings: wallet.lowBalanceCrossings },
            },
          };
        }),

      markTopupInitiated: ({ userId, packCenti }) =>
        // Persist the origin-keyed marker BEFORE the buy hard-nav (gate a). NO balance
        // snapshot — the confirmation is never a diff. A fresh buy supersedes any prior.
        set({ pendingTopup: { userId, packCenti, ts: Date.now() } }),

      onTopupSinceFact: ({ userId, sinceCenti }) =>
        set((s) => {
          const m = s.pendingTopup;
          // Gate a: a marker for THIS user must exist. No marker → nothing to confirm.
          if (!m || m.userId !== userId) return {};
          if (Date.now() - m.ts >= TOPUP_MARKER_TTL_MS) {
            // Stale marker — ignore AND clear (a topup that never completed never fires).
            return { pendingTopup: null };
          }
          // Gate b (backstop): the server topup-since fact. sinceCenti<=0 means the
          // webhook hasn't landed yet — keep the marker, the caller keeps polling.
          if (!(sinceCenti > 0)) return {};
          const prev = s.wallets[userId];
          // Display the MARKER's packCenti (backstop sum = paid-only; marker = paid+bonus).
          const wallet: CreditsWallet = prev
            ? {
                ...prev,
                topupSuccessCrossings: (prev.topupSuccessCrossings ?? 0) + 1,
                lastTopupSuccessCenti: m.packCenti,
              }
            : {
                balanceCenti: 0,
                heldCenti: 0,
                lastTopUpPackCenti: null,
                live: false,
                phase: 'uninitialized',
                lowBalanceCrossings: 0,
                acknowledgedCrossings: 0,
                topupSuccessCrossings: 1,
                acknowledgedTopupCrossings: 0,
                lastTopupSuccessCenti: m.packCenti,
              };
          return { wallets: { ...s.wallets, [userId]: wallet }, pendingTopup: null };
        }),

      acknowledgeTopupSuccess: () =>
        set((s) => {
          const id = s.activeUserId;
          const wallet = id ? s.wallets[id] : undefined;
          if (!id || !wallet) return {};
          return {
            wallets: {
              ...s.wallets,
              [id]: { ...wallet, acknowledgedTopupCrossings: wallet.topupSuccessCrossings ?? 0 },
            },
          };
        }),
    }),
    {
      name: 'va-credits-v1',
      storage: createJSONStorage(() => localStorage),
      version: 1,
      // Numbers persist for continuity; `live` is forced false so a rehydrated
      // wallet can NEVER render as a live balance claim (dark until the server
      // speaks). Modal state / pending buffer / binding are session-transient.
      // `pendingTopup` MUST persist — the buy hard-nav to Stripe destroys component
      // state; only this survives to key the post-return confirmation. The topup
      // crossings ride inside `wallets`, so they survive too (dark contract intact:
      // the crossing counter is orthogonal to `live`).
      partialize: (state) => ({
        wallets: Object.fromEntries(
          Object.entries(state.wallets).map(([id, w]) => [id, { ...w, live: false }]),
        ),
        pendingTopup: state.pendingTopup,
      }),
    },
  ),
);

// ─── Selectors (components render machine state — they never own it) ──────────

/** Wallet phase for the active user; 'uninitialized' unless live THIS session. */
export const selectCreditsPhase = (s: CreditsStore): CreditsPhase => {
  const wallet = s.activeUserId ? s.wallets[s.activeUserId] : undefined;
  return wallet && wallet.live ? wallet.phase : 'uninitialized';
};

/** Balance pill renders ⇔ true — false until the first CREDIT_BALANCE arrives. */
export const selectCreditsPillVisible = (s: CreditsStore): boolean =>
  selectCreditsPhase(s) !== 'uninitialized';

/** Available centicredits (balanceCenti - heldCenti); null while uninitialized —
 *  the store never synthesizes a balance. */
export const selectAvailableCenti = (s: CreditsStore): number | null => {
  const wallet = s.activeUserId ? s.wallets[s.activeUserId] : undefined;
  return wallet && wallet.live ? wallet.balanceCenti - wallet.heldCenti : null;
};

/** Display string for the available balance in CREDITS (centi ÷ 100), max 2
 *  decimals with trailing zeros trimmed (1540 → "15.4", 5000 → "50"); null
 *  while uninitialized. */
export const selectBalanceCredits = (s: CreditsStore): string | null => {
  const centi = selectAvailableCenti(s);
  return centi == null ? null : formatCredits(centi);
};

/** True while a low crossing is live and not yet dismissed — drives the notice. */
export const selectLowBalanceNoticePending = (s: CreditsStore): boolean => {
  const wallet = s.activeUserId ? s.wallets[s.activeUserId] : undefined;
  if (!wallet || !wallet.live || wallet.phase !== 'low') return false;
  return wallet.lowBalanceCrossings > (wallet.acknowledgedCrossings ?? 0);
};

/** True while a topup-success crossing is unacknowledged — drives the success notice.
 *  Deliberately does NOT require `live`: the whole point of the backstop is the
 *  common case where the live emit was dropped by the socket-replay hole, so the
 *  confirmation must be renderable off the persisted crossing alone. The crossing
 *  only EVER ticks behind the two gates (marker + server topup fact), so this can
 *  never fire on a stale/persisted number or a balance diff. */
export const selectTopupSuccessNoticePending = (s: CreditsStore): boolean => {
  const wallet = s.activeUserId ? s.wallets[s.activeUserId] : undefined;
  if (!wallet) return false;
  return (wallet.topupSuccessCrossings ?? 0) > (wallet.acknowledgedTopupCrossings ?? 0);
};

/** Credits to show in the success toast (marker-sourced packCenti ÷ 100); null when no
 *  topup fact has fired for the active user. */
export const selectTopupSuccessCredits = (s: CreditsStore): string | null => {
  const wallet = s.activeUserId ? s.wallets[s.activeUserId] : undefined;
  if (!wallet || !selectTopupSuccessNoticePending(s)) return null;
  return formatCredits(wallet.lastTopupSuccessCenti ?? 0);
};

/** centicredits → credits display string, 2dp max, trailing zeros trimmed. */
export function formatCredits(centi: number): string {
  return (centi / 100).toFixed(2).replace(/\.?0+$/, '');
}
